// Product slideshow: one image at a time with arrows, thumbnails, a slide counter,
// and a jump to the chosen color's photo when the variant changes.
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class SProductGallery extends HTMLElement {
  #ac;
  #raf = 0;
  #index = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-product-gallery]');
    if (!this.track) return;
    this.slides = [...this.track.children].filter((el) => el.hasAttribute('data-media-id'));
    this.prev = this.querySelector('[data-gallery-prev]');
    this.next = this.querySelector('[data-gallery-next]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.allSlides = this.slides;
    this.allThumbs = this.thumbs;
    const mapEl = this.querySelector('[data-variant-media]');
    // Color filter: photos tagged to a color (data-color) only show while that color is chosen.
    this.colorFilter = Boolean(mapEl?.hasAttribute('data-color-filter'));
    this.variantColor = new Map(
      (mapEl?.dataset.variantColor || '')
        .split(/\s+/)
        .filter(Boolean)
        .map((pair) => pair.split(':'))
    );
    this.variantMedia = new Map(
      (this.querySelector('[data-variant-media]')?.dataset.variantMedia || '')
        .split(/\s+/)
        .filter(Boolean)
        .map((pair) => pair.split(':'))
    );

    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.prev?.addEventListener('click', () => this.goTo(this.#index - 1), { signal });
    this.next?.addEventListener('click', () => this.goTo(this.#index + 1), { signal });
    this.allThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === thumb.dataset.mediaId);
        this.goTo(index >= 0 ? index : Number(thumb.dataset.galleryThumb));
      }, { signal });
    });
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => this.#sync());
    }, { signal, passive: true });
    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        const rtl = getComputedStyle(this.track).direction === 'rtl';
        const forward = (event.key === 'ArrowRight') !== rtl;
        event.preventDefault();
        this.goTo(this.#index + (forward ? 1 : -1));
      }
    }, { signal });

    // Choosing a color slides to that variant's photo.
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;
      if (!input.closest('s-variant-picker')) return;
      const variantId = input instanceof HTMLSelectElement
        ? input.selectedOptions[0]?.dataset.variantId
        : input.dataset.variantId;
      this.#showVariant(variantId);
    }, { signal });

    const initialVariant = new URL(window.location.href).searchParams.get('variant') || mapEl?.dataset.initialVariant;
    if (initialVariant) this.#showVariant(initialVariant, true);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#raf);
  }

  #applyColor(color) {
    if (!this.colorFilter || !color) return false;
    // A color with its own lifestyle photos shows only its photos; otherwise shared photos stay too.
    const ownSet = this.allSlides.some((el) => el.hasAttribute('data-lifestyle') && el.dataset.color === color);
    const visible = (el) => el.dataset.color === color || (!ownSet && !el.dataset.color);
    this.allSlides.forEach((slide) => { slide.hidden = !visible(slide); });
    this.allThumbs.forEach((thumb) => { thumb.hidden = !visible(thumb); });
    this.slides = this.allSlides.filter(visible);
    this.thumbs = this.allThumbs.filter(visible);
    return true;
  }

  #showVariant(variantId, instant = false) {
    const filtered = variantId && this.#applyColor(this.variantColor.get(String(variantId)));
    const mediaId = variantId && this.variantMedia.get(String(variantId));
    let index = filtered ? 0 : -1;
    if (mediaId) {
      const match = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (match >= 0) index = match;
    }
    if (index < 0) return;
    // Color changes jump straight to the photo (no slide past every image in between), with a quick fade.
    this.goTo(index, true);
    if (!instant && !reduceMotion.matches) {
      this.track.animate([{ opacity: 0.4 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
    }
  }

  goTo(index, instant = false) {
    if (!this.slides.length) return;
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    // Every slide is exactly one track-width wide, so the offset is index × width (negative in RTL).
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    const left = clamped * this.track.clientWidth * (rtl ? -1 : 1);
    this.track.scrollTo({ left, behavior: instant || reduceMotion.matches ? 'instant' : 'smooth' });
    this.#index = clamped;
    this.#render();
  }

  #sync() {
    const width = this.track.clientWidth || 1;
    this.#index = Math.max(0, Math.min(Math.round(Math.abs(this.track.scrollLeft) / width), this.slides.length - 1));
    this.#render();
  }

  #render() {
    const total = this.slides.length;
    if (this.counter) this.counter.textContent = `${this.#index + 1} / ${total}`;
    if (this.prev) this.prev.disabled = this.#index <= 0;
    if (this.next) this.next.disabled = this.#index >= total - 1;
    this.thumbs.forEach((thumb, i) => {
      if (i === this.#index) {
        thumb.setAttribute('aria-current', 'true');
        // Keep the active thumb in view by scrolling only the strip, never the page.
        const strip = thumb.parentElement;
        if (strip) {
          const vertical = strip.scrollHeight > strip.clientHeight + 1 && strip.scrollWidth <= strip.clientWidth + 1;
          if (vertical) {
            const top = thumb.offsetTop - strip.offsetTop;
            if (top < strip.scrollTop || top + thumb.offsetHeight > strip.scrollTop + strip.clientHeight) {
              strip.scrollTop = top - (strip.clientHeight - thumb.offsetHeight) / 2;
            }
          } else {
            const start = thumb.offsetLeft - strip.offsetLeft;
            if (start < strip.scrollLeft || start + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
              strip.scrollLeft = start - (strip.clientWidth - thumb.offsetWidth) / 2;
            }
          }
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', SProductGallery);
